import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import type { DevToolsPlugin } from '../../types';
import { styles } from './msw-mocks.styles';
import type { DynamicMockDefinition, ErrorType, HttpMethod } from './msw-mocks.types';
import { getMocks } from './msw-worker';

const HTTP_METHODS: HttpMethod[] = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

@customElement('acme-devtools-msw')
export class AcmeDevToolsMsw extends LitElement {
  static override styles = styles;

  @state() private _definitions: DynamicMockDefinition[] = [];
  @state() private _formOpen = false;
  @state() private _editingId: string | null = null;

  // Form fields
  @state() private _label = '';
  @state() private _method: HttpMethod = 'GET';
  @state() private _urlPattern = '';
  @state() private _statusCode = 200;
  @state() private _responseBody = '{}';
  @state() private _contentType: 'json' | 'text' = 'json';
  @state() private _delayMs = 0;
  @state() private _errorType: ErrorType = 'none';

  private get _msw() {
    return (window as any).__ACME_B2B__?.devTools?.msw;
  }

  override connectedCallback() {
    super.connectedCallback();
    this._refresh();
  }

  private _refresh() {
    this._definitions = this._msw?.getMocks() ?? [];
  }

  private _toggleForm() {
    if (this._formOpen && this._editingId) {
      this._cancelEdit();
    } else {
      this._formOpen = !this._formOpen;
      if (!this._formOpen) {
        this._resetFormFields();
      }
    }
  }

  private _resetFormFields() {
    this._label = '';
    this._method = 'GET';
    this._urlPattern = '';
    this._statusCode = 200;
    this._responseBody = '{}';
    this._contentType = 'json';
    this._delayMs = 0;
    this._errorType = 'none';
    this._editingId = null;
  }

  private _startEdit(def: DynamicMockDefinition) {
    this._editingId = def.id;
    this._label = def.label;
    this._method = def.method;
    this._urlPattern = def.urlPattern;
    this._statusCode = def.statusCode;
    this._responseBody = def.responseBody;
    this._contentType = def.contentType;
    this._delayMs = def.delayMs;
    this._errorType = def.errorType;
    this._formOpen = true;
  }

  private _cancelEdit() {
    this._resetFormFields();
    this._formOpen = false;
  }

  private _saveEdit() {
    if (!this._urlPattern.trim() || !this._msw || !this._editingId) {
      return;
    }
    this._msw.updateMock(this._editingId, {
      method: this._method,
      urlPattern: this._urlPattern.trim(),
      statusCode: this._errorType !== 'none' ? 0 : this._statusCode,
      responseBody: this._responseBody,
      contentType: this._contentType,
      delayMs: this._delayMs,
      errorType: this._errorType,
      label: this._label.trim(),
    });
    this._resetFormFields();
    this._formOpen = false;
    this._refresh();
  }

  private _addMock() {
    if (!this._urlPattern.trim() || !this._msw) {
      return;
    }
    this._msw.addMock({
      method: this._method,
      urlPattern: this._urlPattern.trim(),
      statusCode: this._errorType !== 'none' ? 0 : this._statusCode,
      responseBody: this._responseBody,
      contentType: this._contentType,
      delayMs: this._delayMs,
      errorType: this._errorType,
      enabled: true,
      label: this._label.trim(),
    });
    this._resetFormFields();
    this._formOpen = false;
    this._refresh();
  }

  private _toggle(id: string) {
    this._msw?.toggleMock(id);
    this._refresh();
  }

  private _remove(id: string) {
    this._msw?.removeMock(id);
    this._refresh();
  }

  private _clearAll() {
    this._msw?.clearMocks();
    this._refresh();
  }

  private _renderForm() {
    return html`
      <div class="form">
        <div class="form-row">
          <span class="form-label">Label</span>
          <input
            class="form-input"
            type="text"
            placeholder="Optional description"
            .value=${this._label}
            @input=${(e: Event) => (this._label = (e.target as HTMLInputElement).value)} />
        </div>

        <div class="form-row">
          <span class="form-label">Method</span>
          <select
            class="form-select"
            .value=${this._method}
            @change=${(e: Event) => (this._method = (e.target as HTMLSelectElement).value as HttpMethod)}>
            ${HTTP_METHODS.map((m) => html`<option value=${m}>${m}</option>`)}
          </select>
        </div>

        <div class="form-row">
          <span class="form-label">URL Pattern</span>
          <input
            class="form-input"
            type="text"
            placeholder="/v1/api/events/:id"
            .value=${this._urlPattern}
            @input=${(e: Event) => (this._urlPattern = (e.target as HTMLInputElement).value)} />
        </div>

        <div class="form-row">
          <span class="form-label">Error Type</span>
          <select
            class="form-select"
            .value=${this._errorType}
            @change=${(e: Event) => (this._errorType = (e.target as HTMLSelectElement).value as ErrorType)}>
            <option value="none">None</option>
            <option value="network">Network error (connection refused)</option>
            <option value="cors">CORS error</option>
          </select>
        </div>

        ${this._errorType !== 'none'
          ? nothing
          : html`
              <div class="form-row">
                <span class="form-label">Status</span>
                <input
                  class="form-input"
                  type="number"
                  min="100"
                  max="599"
                  .value=${String(this._statusCode)}
                  @input=${(e: Event) => (this._statusCode = Number((e.target as HTMLInputElement).value))} />
              </div>

              <div class="form-row">
                <span class="form-label">Body</span>
                <textarea
                  class="form-textarea"
                  .value=${this._responseBody}
                  @input=${(e: Event) => (this._responseBody = (e.target as HTMLTextAreaElement).value)}></textarea>
              </div>

              <div class="form-row">
                <span class="form-label">Type</span>
                <select
                  class="form-select"
                  .value=${this._contentType}
                  @change=${(e: Event) =>
                    (this._contentType = (e.target as HTMLSelectElement).value as 'json' | 'text')}>
                  <option value="json">JSON</option>
                  <option value="text">Text</option>
                </select>
              </div>
            `}

        <div class="form-row">
          <span class="form-label">Delay (ms)</span>
          <input
            class="form-input"
            type="number"
            min="0"
            .value=${String(this._delayMs)}
            @input=${(e: Event) => (this._delayMs = Number((e.target as HTMLInputElement).value))} />
        </div>

        <div class="form-footer">
          ${this._editingId
            ? html`
                <button class="btn btn-cancel" @click=${() => this._cancelEdit()}>Cancel</button>
                <button class="btn btn-save" ?disabled=${!this._urlPattern.trim()} @click=${() => this._saveEdit()}>
                  Save Changes
                </button>
              `
            : html`
                <button class="btn btn-add" ?disabled=${!this._urlPattern.trim()} @click=${() => this._addMock()}>
                  Add Mock
                </button>
              `}
        </div>
      </div>
    `;
  }

  override render() {
    const hasMocks = this._definitions.length > 0;

    return html`
      <div class="toolbar">
        <button
          class="btn ${hasMocks ? 'btn-danger' : 'btn-disabled'}"
          ?disabled=${!hasMocks}
          @click=${() => this._clearAll()}>
          Clear All
        </button>
        ${hasMocks
          ? html`<span class="mock-count"
              >${this._definitions.length} mock${this._definitions.length === 1 ? '' : 's'}</span
            >`
          : nothing}
      </div>

      <h3 class="section-heading" @click=${() => this._toggleForm()}>
        ${this._formOpen ? '▾' : '▸'} ${this._editingId ? 'Edit Mock' : 'Add Mock'}
      </h3>

      ${this._formOpen ? this._renderForm() : nothing}

      <h3 class="section-heading">Active Mocks</h3>

      ${hasMocks
        ? this._definitions.map(
            (def) => html`
              <div
                class="mock-row ${def.enabled ? '' : 'mock-row--disabled'} ${this._editingId === def.id
                  ? 'mock-row--editing'
                  : ''}">
                <span class="method-badge method-${def.method}">${def.method}</span>
                <div style="min-width:0">
                  <div class="mock-url">${def.urlPattern}</div>
                  ${def.label ? html`<div class="mock-label">${def.label}</div>` : nothing}
                </div>
                <span class="mock-status">
                  ${def.errorType === 'cors' ? 'CORS' : def.errorType === 'network' ? 'ERR' : def.statusCode}
                  ${def.delayMs > 0 ? html`+${def.delayMs}ms` : nothing}
                </span>
                <button
                  class="toggle-btn ${def.enabled ? 'toggle-btn--on' : 'toggle-btn--off'}"
                  @click=${() => this._toggle(def.id)}>
                  ${def.enabled ? 'On' : 'Off'}
                </button>
                <button class="edit-btn" @click=${() => this._startEdit(def)}>✎</button>
                <button class="delete-btn" @click=${() => this._remove(def.id)}>✕</button>
              </div>
            `,
          )
        : html`<div class="empty">No mocks configured. Add one above or use the console API.</div>`}
    `;
  }
}

export const mswPlugin: DevToolsPlugin = {
  id: 'msw',
  label: 'MSW',
  tagName: 'acme-devtools-msw',
  badge: () => {
    const active = getMocks().filter((m) => m.enabled).length;
    return active > 0 ? `${active} active mock${active === 1 ? '' : 's'} intercepting requests` : null;
  },
};

declare global {
  interface HTMLElementTagNameMap {
    'acme-devtools-msw': AcmeDevToolsMsw;
  }
}
